import { TimelineItem as MantineTimelineItem } from "@mantine/core";
import { componentWrapper } from "../../utilities";
import type { CustomTimelineItemFactory, ITimelineItemProps } from "./timeline.types";
import { Text } from "../text";
import styles from "./timeline.module.scss";
import { Icon } from "../icon";

/**
 * TimelineItem component wrapper.
 *
 * @prop title - The title of the timeline item. Can be a string or a React node.
 * @prop subtitle - The subtitle of the timeline item. Can be a string or a React node.
 * @prop footer - The footer of the timeline item. Can be a string or a React node.
 * @prop icon - The icon to display in the timeline item. Can be a FontAwesome icon or a React node.
 * @prop bullet - The custom bullet to display in the timeline item. Overrides the icon if provided.
 * @prop color - The color of the timeline item. Can be "primary", "error", "success", "warning", or "info". Default is "primary".
 * @prop children - The content of the timeline item. Can be a string or a React node.
 * @prop classNames - Custom class names for the timeline item. Can be an object with the following properties: itemBody, itemContent, itemBullet, item, itemTitle.
 */
const TimelineItem = componentWrapper<CustomTimelineItemFactory, ITimelineItemProps>(
    "TimelineItem",
    ({ title, subtitle, footer, icon, bullet, children, classNames, color = "primary", ...props }, ref) => {
        return (
            <MantineTimelineItem
                ref={ref}
                classNames={{
                    itemBody: styles["itemBody"],
                    itemContent: styles["itemContent"],
                    itemBullet: styles["itemBullet"],
                    item: styles["item"],
                    itemTitle: styles["itemTitle"],
                    ...(classNames as any),
                }}
                title={
                    typeof title !== "string" ? (
                        title
                    ) : (
                        <Text variant="semibold" size="sm">
                            {title}
                        </Text>
                    )
                }
                data-color={color}
                bullet={bullet || (typeof icon !== "undefined" ? <Icon icon={icon} size="xs" /> : undefined)}
                {...props}
            >
                {subtitle && (
                    <Text size="sm" className={styles["subtitle"]}>
                        {subtitle}
                    </Text>
                )}
                {footer && (
                    <Text dimmed size="xs" mt="sm" className={styles["footer"]}>
                        {footer}
                    </Text>
                )}
                {children}
            </MantineTimelineItem>
        );
    }
);

export default TimelineItem;
